/**
 * Import a peer from a bundle on the operator's workstation (REQ-UI-150;
 * REQ-PEER-103; storyboard Flow W §5c).
 *
 * # Review, then install — two acts, not one
 *
 * The operator picks a file; this dialog reads it **locally** and shows what the
 * archive contains — the peer, its interfaces, each interface's connections, and
 * every artifact with the digest the installer will check. Nothing is sent until
 * they confirm. Reading a bundle a partner site handed you must not be the same
 * act as installing it, and the BFF's import route has no dry-run: it unpacks
 * and installs. So the preview is a local parse (`peer-bundle-preview.ts`),
 * which also means it costs no request at all.
 *
 * # The install button is visible and disabled, and that is deliberate
 *
 * REQ-UI-143's absent-not-disabled rule governs **permissions**. This is the
 * other case the peer surface already draws a line for: a capability the
 * operator *has*, which the surface cannot perform yet. Hiding it would conceal
 * a gap in the bundle rather than a privilege the session lacks — so it stays,
 * disabled, with the reason stated.
 *
 * # The outcome is reported, not collapsed
 *
 * This import is the one deliberate path that can re-apply a bundle (REQ-PEER-102
 * as amended 2026-10-07; the startup pass never does). So it has four outcomes,
 * and the dialog keeps them apart: **installed** (new, or a peer this deployment
 * deleted and the bundle re-created), **replaced** (the peer existed and its
 * configuration is now the bundle's), **already applied** (same bundle, peer
 * still here — nothing changed, truthfully), and **refused** (with the reason).
 * A bare success would make the first three look alike, and an operator who
 * just overwrote a live egress path must be told so.
 *
 * Cross-references:
 *   - REQ-UI-150; REQ-PEER-102 / -103; feature-spec D-1, D-8, D-15.
 *   - `peer-bundle-preview.ts` (the local parse); `use-peers.ts` (the POST).
 */
import * as Dialog from "@radix-ui/react-dialog";
import { useRef, useState } from "react";
import {
  BundleUnreadable,
  isInstallable,
  parsePeerBundle,
  type BundlePreview,
} from "@/peers/peer-bundle-preview";
import { useImportPeerBundle, type PeerImportOutcome } from "@/peers/use-peers";
import { cn } from "@/lib/utils";

/** Props for {@link PeerImportDialog}. */
export interface PeerImportDialogProps {
  onClose: () => void;
  /** Called with the installed peer's name once the BFF reports it applied. */
  onInstalled: (peerName: string) => void;
}

/** Render the import review dialog. */
export function PeerImportDialog({
  onClose,
  onInstalled,
}: PeerImportDialogProps): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<BundlePreview | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<PeerImportOutcome | null>(null);
  const importBundle = useImportPeerBundle();

  const choose = async (chosen: File | undefined): Promise<void> => {
    if (chosen === undefined) return;
    setFile(chosen);
    setPreview(null);
    setOutcome(null);
    setReadError(null);
    try {
      setPreview(await parsePeerBundle(await chosen.arrayBuffer()));
    } catch (caught) {
      // The parser's messages are written FOR an operator, so they surface
      // verbatim. Anything else is generic (REQ-UI-037 — no internal leakage).
      setReadError(
        caught instanceof BundleUnreadable ? caught.message : "This file could not be read.",
      );
    }
  };

  const installable = preview !== null && isInstallable(preview);

  return (
    <Dialog.Root open onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/60" />
        <Dialog.Content
          data-testid="peer-import-dialog"
          aria-describedby={undefined}
          className={cn(
            "system-status-scope fixed left-1/2 top-1/2 z-[60] flex max-h-[82vh] w-[760px]",
            "max-w-[92vw] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-md",
            "border border-border bg-bg-surface shadow-lg",
          )}
        >
          <header className="flex items-center gap-3 border-b border-border px-4 py-2">
            <Dialog.Title className="text-body font-medium text-text-primary">
              Import peer
            </Dialog.Title>
            <span className="text-meta text-text-secondary">Review before installing</span>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close import"
                data-testid="peer-import-close"
                className="ml-auto rounded p-1 text-text-secondary hover:bg-bg-elevated"
              >
                ✕
              </button>
            </Dialog.Close>
          </header>

          <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
            <input
              ref={inputRef}
              type="file"
              accept=".gz,.tgz,application/gzip"
              className="hidden"
              data-testid="peer-import-file"
              onChange={(event) => void choose(event.target.files?.[0])}
            />
            <div className="flex items-center gap-2 pb-3">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                data-testid="peer-import-choose"
                className="rounded border border-border px-2 py-1 text-body-sm text-text-primary hover:bg-bg-elevated"
              >
                Choose bundle…
              </button>
              <span className="text-meta text-text-muted" data-testid="peer-import-filename">
                {file === null ? "No file selected." : file.name}
              </span>
            </div>

            {readError !== null && (
              <p
                role="alert"
                data-testid="peer-import-unreadable"
                className="text-body-sm text-destructive"
              >
                {readError}
              </p>
            )}

            {preview !== null && outcome === null && (
              <PreviewBody preview={preview} installable={installable} />
            )}

            {outcome !== null && <OutcomeBody outcome={outcome} />}
          </div>

          <footer className="flex items-center gap-2 border-t border-border px-4 py-2">
            <span className="text-meta text-text-muted" data-testid="peer-import-footnote">
              {outcome !== null
                ? "The installer's report is above."
                : preview === null
                  ? "Nothing is sent until you choose a bundle and confirm."
                  : installable
                    ? "Installs one peer, its interfaces and its connections — replacing them if this deployment already has it — and records the act."
                    : "Install is unavailable until the problems above are resolved."}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                data-testid="peer-import-cancel"
                className="rounded border border-border px-3 py-1 text-body-sm text-text-primary hover:bg-bg-elevated"
              >
                {outcome === null ? "Cancel" : "Close"}
              </button>
              {outcome === null && (
                <button
                  type="button"
                  // Visible and disabled — see the module docstring. This is the
                  // capability-gap case, not the permission case.
                  disabled={!installable || file === null || importBundle.isPending}
                  data-testid="peer-import-confirm"
                  onClick={() => {
                    if (file === null) return;
                    importBundle.mutate(file, {
                      onSuccess: (result) => {
                        setOutcome(result);
                        if (result.applied.length > 0 && result.peer !== null) {
                          onInstalled(result.peer.name);
                        }
                      },
                    });
                  }}
                  className="rounded bg-brand px-3 py-1 text-body-sm font-medium text-bg-base hover:bg-brand-dim disabled:opacity-50"
                >
                  {importBundle.isPending ? "Installing…" : "Install peer"}
                </button>
              )}
            </div>
          </footer>

          {importBundle.isError && (
            <p
              role="alert"
              data-testid="peer-import-error"
              className="px-4 pb-2 text-meta text-destructive"
            >
              {describeImportError(importBundle.error.status, importBundle.error.body)}
            </p>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** What the archive contains, shown before anything is written. */
function PreviewBody({
  preview,
  installable,
}: {
  preview: BundlePreview;
  installable: boolean;
}): React.ReactElement {
  const unresolved = preview.placeholders.filter((p) => p.unresolved);
  return (
    <div className="flex flex-col gap-3" data-testid="peer-import-preview">
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-body-sm">
        <dt className="text-text-secondary">Bundle</dt>
        <dd className="font-mono text-text-primary">{preview.bundleName}</dd>
        <dt className="text-text-secondary">Peer</dt>
        <dd className="font-mono text-text-primary">{preview.peerName}</dd>
        {preview.peerDescription !== null && (
          <>
            <dt className="text-text-secondary">Description</dt>
            <dd className="text-text-primary">{preview.peerDescription}</dd>
          </>
        )}
      </dl>

      {unresolved.length > 0 && (
        <div
          role="alert"
          data-testid="peer-import-blocked"
          className="rounded border border-destructive px-3 py-2 text-meta text-destructive"
        >
          <p className="pb-1">
            <strong>
              {unresolved.length} unresolved placeholder{unresolved.length === 1 ? "" : "s"}.
            </strong>{" "}
            This bundle ships with values the receiving site fills in, and{" "}
            {unresolved.length === 1 ? "one has" : "these have"} not been. Installing now would
            store the placeholder text itself as configuration, and the interface would fail to
            start later with nothing pointing back here.
          </p>
          <ul className="flex flex-col gap-0.5">
            {unresolved.map((placeholder) => (
              <li key={placeholder.token} className="font-mono">
                ${"{"}
                {placeholder.token}
                {"}"}
                {placeholder.prompt !== "" && (
                  <span className="font-sans"> — {placeholder.prompt}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {preview.digestProblems.length > 0 && (
        <div
          role="alert"
          data-testid="peer-import-digest-problem"
          className="rounded border border-destructive px-3 py-2 text-meta text-destructive"
        >
          <p className="pb-1">
            <strong>This archive does not match its own manifest.</strong> It is not the bundle the
            manifest describes, which the deployment will refuse as well.
          </p>
          <ul className="flex flex-col gap-0.5">
            {preview.digestProblems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      )}

      {installable && (
        <p className="text-meta text-text-muted" data-testid="peer-import-ready">
          Every placeholder in this bundle is filled in and every member matches its digest. Nothing
          has been written yet.
        </p>
      )}

      <section>
        <h3 className="pb-1 text-body-sm font-medium text-text-primary">
          Interfaces and connections
        </h3>
        {preview.interfaces.length === 0 ? (
          <p className="text-body-sm text-text-muted" data-testid="peer-import-no-interfaces">
            This bundle configures no interfaces, so the peer it installs has no path to send on.
          </p>
        ) : (
          <ul className="flex flex-col gap-1" data-testid="peer-import-interfaces">
            {preview.interfaces.map((iface) => (
              <li key={iface.name} className="border-b border-border py-1 last:border-b-0">
                <div className="flex flex-wrap items-baseline gap-2 text-body-sm">
                  <span className="font-mono text-text-primary">{iface.name}</span>
                  <span className="text-meta text-text-secondary">{iface.typeId}</span>
                  {iface.transportSecurity !== null && (
                    <span className="text-meta text-text-secondary">
                      · {iface.transportSecurity}
                    </span>
                  )}
                  {iface.conversionMode !== null && (
                    <span className="text-meta text-text-secondary">· {iface.conversionMode}</span>
                  )}
                  {iface.hasUnresolved && (
                    <span className="text-meta text-destructive">— unresolved placeholder</span>
                  )}
                </div>
                {iface.connections.map((connection) => (
                  <div
                    key={connection.name}
                    data-testid={`peer-import-connection-${connection.name}`}
                    className="pl-4 text-meta text-text-secondary"
                  >
                    ↳ <span className="font-mono">{connection.name}</span>{" "}
                    {connection.endpointHost !== null ? (
                      <span
                        className={cn("font-mono", connection.hasUnresolved && "text-destructive")}
                      >
                        → {connection.endpointHost}
                        {connection.endpointPort !== null ? `:${connection.endpointPort}` : ""}
                      </span>
                    ) : (
                      <span>
                        {connection.role === "pull" ? "Consumer pulls" : "Meridian dials out"}
                        {connection.endpointPort !== null ? ` · :${connection.endpointPort}` : ""}
                      </span>
                    )}
                    {/* Marked on the ROW that carries it, not only in the summary
                        above: an operator fixing one needs to see which
                        connection it belongs to. */}
                    {connection.hasUnresolved && (
                      <span className="text-destructive"> — unresolved placeholder</span>
                    )}
                  </div>
                ))}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="pb-1 text-body-sm font-medium text-text-primary">Artifacts</h3>
        <ul className="flex flex-col gap-1" data-testid="peer-import-artifacts">
          {preview.artifacts.map((artifact) => (
            <li key={artifact.member} className="text-meta">
              <span className="font-mono text-text-primary">{artifact.member}</span>{" "}
              <span className="text-text-secondary">{artifact.kind}</span>
              <div
                className={cn(
                  "break-all font-mono",
                  artifact.actualSha256 === artifact.declaredSha256
                    ? "text-text-muted"
                    : "text-destructive",
                )}
              >
                sha256:{artifact.declaredSha256}
              </div>
            </li>
          ))}
        </ul>
        <p className="pt-1 text-meta text-text-muted">
          Each digest is what the installer verifies the member against, so an archive altered in
          transit is refused rather than installed.
        </p>
      </section>

      <p className="text-meta text-text-muted" data-testid="peer-import-apply-once">
        Importing is deliberate, and it is the only act that re-applies a bundle. A peer this
        deployment deleted is re-created; a peer whose bundle has changed is replaced — its
        configuration becomes what the bundle states, and interfaces the bundle no longer carries
        are removed; an unchanged bundle for a peer still here changes nothing. Restarts and
        upgrades never re-apply a bundle on their own.
      </p>
    </div>
  );
}

/** The installer's verdict, with the four outcomes kept apart. */
function OutcomeBody({ outcome }: { outcome: PeerImportOutcome }): React.ReactElement {
  // `replaced` is a subset of `applied`; what is left is a genuine install (new, or
  // re-created after a delete — the installer does not distinguish those, and to the
  // operator both are "it is here now").
  const installed = outcome.applied.filter((name) => !outcome.replaced.includes(name));
  return (
    <div className="flex flex-col gap-2 text-body-sm" data-testid="peer-import-outcome">
      {installed.length > 0 && (
        <p className="text-text-primary" data-testid="peer-import-applied">
          Installed <span className="font-mono">{installed.join(", ")}</span>.
        </p>
      )}
      {outcome.replaced.length > 0 && (
        <p className="text-text-primary" data-testid="peer-import-replaced">
          Replaced <span className="font-mono">{outcome.replaced.join(", ")}</span> — the peer's
          configuration is now what this bundle states. Its interfaces reconcile to the new
          configuration.
        </p>
      )}
      {outcome.already_applied.length > 0 && (
        <p className="text-text-secondary" data-testid="peer-import-already-applied">
          <span className="font-mono">{outcome.already_applied.join(", ")}</span> was already
          applied to this deployment and its peer is still installed as the bundle states. Nothing
          changed.
        </p>
      )}
      {outcome.rejected.length > 0 && (
        <div role="alert" data-testid="peer-import-rejected" className="text-destructive">
          <p className="pb-1">Refused, and nothing was written:</p>
          <ul className="flex flex-col gap-0.5">
            {outcome.rejected.map((rejection) => (
              <li key={rejection.bundle}>
                <span className="font-mono">{rejection.bundle}</span> — {rejection.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
      {outcome.applied.length === 0 &&
        outcome.already_applied.length === 0 &&
        outcome.rejected.length === 0 && (
          <p className="text-text-secondary" data-testid="peer-import-nothing">
            The deployment applied nothing from this bundle and reported no reason. Nothing changed.
          </p>
        )}
    </div>
  );
}

/**
 * Map an import failure to operator-facing copy.
 *
 * The BFF's typed refusals carry a `message` written for this surface, so it is
 * shown verbatim where present; everything else is generic (REQ-UI-037).
 */
function describeImportError(status: number, body: unknown): string {
  if (status === 422 && typeof body === "object" && body !== null) {
    const detail = (body as { detail?: unknown }).detail;
    if (typeof detail === "object" && detail !== null) {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === "string") return `The deployment refused this bundle: ${message}`;
    }
  }
  if (status === 403) return "This session may not import peers.";
  return "Could not import this bundle. Nothing was installed.";
}
